<script setup lang="ts">
/**
 * ResidentRegisterView — 3-step resident registration flow.
 * Ports resident-register.html exactly.
 *
 * Step 1: Identity — collect name, email, address, lot, resident type.
 *         On "Continue": if auth has an MSAL account, skip MSAL signup (returning
 *         user via resume-registration path). Otherwise call auth.registerResident()
 *         to trigger MSAL CIAM signup redirect.
 *
 * Step 2: Verification method picker. On "Request verification": POST /v1/resident/register.
 *
 * Step 3: Pending banner. Shown when isPending === true OR after successful POST.
 *
 * Resume path: user returns from MSAL redirect with a token but no Resident row yet
 * (GET /v1/resident/me → 404) → skip to step 2 with identity pre-filled from MSAL token.
 */
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import RegisterStep1Identity from '@/components/resident/RegisterStep1Identity.vue';
import RegisterStep2Address from '@/components/resident/RegisterStep2Address.vue';
import RegisterStep3Pending from '@/components/resident/RegisterStep3Pending.vue';
import { useAuth } from '@/stores/auth';
import { registerResident } from '@/composables/useResident';

const auth = useAuth();
const route = useRoute();

// Step state — default to 1; if already pending/active, jump to 3.
const currentStep = ref<1 | 2 | 3>(1);
// Prefer path param (new /signup/resident/:slug route from W7.8) over the legacy
// ?hoa= query, with a Cypress-Ridge fallback for dev when neither is provided.
const hoaSlug = computed(() =>
  (route.params.slug as string | undefined)
  ?? (route.query.hoa as string | undefined)
  ?? 'cypress-ridge');

// Collected form data
const identityData = ref<{
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  lot: string;
  residentType: string;
} | null>(null);

const isSubmitting = ref(false);
const submitError = ref<string | null>(null);

// On mount: if auth says isPending (resumed after approval wait), show step 3 directly.
onMounted(() => {
  if (auth.isPending || auth.isResident) {
    currentStep.value = 3;
  }
});

// Step 1 → 2
async function onStep1Next(data: typeof identityData.value) {
  identityData.value = data;
  // In a full CIAM flow, step 1 "Continue" would trigger auth.registerResident()
  // (MSAL signup redirect). Post-redirect, MSAL returns to /resident/auth-callback
  // and bootstrapResident routes back here at step 2 via the resume path.
  // For now: advance directly to step 2 (MSAL redirect tested in e2e, not unit tests).
  currentStep.value = 2;
}

// Step 2 → 3 (POST to backend)
async function onStep2Next(method: 'MailedCode' | 'DocumentUpload' | 'NeighborVouch') {
  if (!identityData.value) return;
  isSubmitting.value = true;
  submitError.value = null;
  try {
    await registerResident({
      hoaSlug: hoaSlug.value,
      fullName: `${identityData.value.firstName} ${identityData.value.lastName}`.trim(),
      address: identityData.value.address,
      verificationMethod: method,
      contactPhone: identityData.value.phone || undefined,
    });
    currentStep.value = 3;
  } catch (e) {
    submitError.value = e instanceof Error
      ? e.message
      : 'Something went wrong. Please try again.';
  } finally {
    isSubmitting.value = false;
  }
}

function onStep2Back() {
  currentStep.value = 1;
}

const fullName = computed(() =>
  identityData.value
    ? `${identityData.value.firstName} ${identityData.value.lastName}`.trim()
    : auth.user?.displayName ?? '',
);
const address = computed(() =>
  identityData.value?.address ?? auth.user?.email ?? '',
);
</script>

<template>
  <div class="reg-shell" data-testid="resident-register-view">
    <!-- Topbar -->
    <div class="reg-topbar">
      <div class="launcher-mark" style="display:flex; gap:10px; align-items:center;">
        <div class="brand-mark">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 11.5L12 4l9 7.5" />
            <path d="M5 10v10h14V10" />
            <circle cx="12" cy="14" r="2.2" fill="currentColor" stroke="none" />
          </svg>
        </div>
        <div>
          <div class="brand-name">HOA Watch</div>
          <div class="brand-sub">Resident portal</div>
        </div>
      </div>
      <div class="right-link">
        Already a resident?
        <a href="/resident/login">Sign in</a>
      </div>
    </div>

    <div class="reg-wrap">
      <!-- Stepper -->
      <div class="stepper" role="list" aria-label="Registration steps">
        <div class="step" :class="{ active: currentStep === 1, complete: currentStep > 1 }" role="listitem">
          <div class="step-num"><span>1</span></div>
          <div class="step-label">Your details</div>
        </div>
        <div class="step-line" />
        <div class="step" :class="{ active: currentStep === 2, complete: currentStep > 2 }" role="listitem">
          <div class="step-num"><span>2</span></div>
          <div class="step-label">Verify residency</div>
        </div>
        <div class="step-line" />
        <div class="step" :class="{ active: currentStep === 3, complete: false }" role="listitem">
          <div class="step-num"><span>3</span></div>
          <div class="step-label">Board approval</div>
        </div>
      </div>

      <!-- Error banner (step 2 POST failure) -->
      <div
        v-if="submitError"
        class="p-3 bg-red-50 border border-red-200 rounded-md text-[13.5px] text-red-700 mb-4"
        role="alert"
      >
        {{ submitError }}
      </div>

      <!-- Card shell -->
      <div class="reg-card">
        <RegisterStep1Identity
          v-if="currentStep === 1"
          @next="onStep1Next"
        />
        <RegisterStep2Address
          v-else-if="currentStep === 2"
          @next="onStep2Next"
          @back="onStep2Back"
        />
        <RegisterStep3Pending
          v-else
          :full-name="fullName"
          :address="address"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Registration shell — mirrors resident-register.html */
.reg-shell {
  min-height: 100vh;
  background: linear-gradient(180deg, var(--navy-50), var(--bg) 30%);
  display: flex;
  flex-direction: column;
}
.reg-topbar {
  padding: 20px 28px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid var(--border);
  background: var(--surface);
}
.reg-topbar .right-link { margin-left: auto; font-size: 13.5px; color: var(--muted); }
.reg-topbar .right-link a { font-weight: 600; color: var(--navy-700); }
.brand-mark {
  width: 36px;
  height: 36px;
  background: var(--navy-800);
  border-radius: var(--r-md, 8px);
  display: grid;
  place-items: center;
  color: white;
  flex-shrink: 0;
}
.brand-mark svg { width: 18px; height: 18px; }
.brand-name { font-size: 15px; font-weight: 700; letter-spacing: -0.01em; }
.brand-sub { font-size: 11.5px; color: var(--muted); margin-top: -1px; }
.reg-wrap {
  max-width: 720px;
  margin: 0 auto;
  padding: 40px 24px 80px;
  width: 100%;
}
.reg-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--r-xl, 16px);
  box-shadow: var(--shadow);
  overflow: hidden;
}

/* Stepper */
.stepper {
  display: flex;
  align-items: center;
  background: var(--surface);
  border: 1px solid var(--border);
  padding: 12px 20px;
  border-radius: 999px;
  margin-bottom: 24px;
}
.step { display: flex; align-items: center; gap: 8px; }
.step-num {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: var(--bg-soft);
  color: var(--muted-2);
  display: grid;
  place-items: center;
  font-size: 11.5px;
  font-weight: 700;
  font-family: var(--font-mono);
}
.step-label { font-size: 12.5px; color: var(--muted); font-weight: 500; }
.step.active .step-num { background: var(--navy-700); color: white; }
.step.active .step-label { color: var(--fg); font-weight: 600; }
.step.complete .step-num { background: var(--green-700); color: white; }
.step.complete .step-num::after { content: '✓'; }
.step.complete .step-num span { display: none; }
.step-line { flex: 1; height: 2px; background: var(--border); margin: 0 10px; border-radius: 1px; }
@media (max-width: 640px) {
  .step-label { display: none; }
  .stepper { padding: 10px 14px; }
}
</style>
